import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AudioDto, GenerateStatus } from '@/service/type';
import { ChevRightFilledIcon, FilterIcon, FolderFilledIcon, HeartIcon, PauseIcon, PlayIcon } from '@/components/Icons';
import CoverDefaultSrc from '@/assets/cover-default.png';
import { cn, displayDuration } from '@/utils';
import { LoaderCircle } from 'lucide-react';
import { useAudioPlayerStore } from '@/state/audio-player-store';
import { useDimensions } from '@/hooks/useDimensions';

import { useDrag } from 'react-dnd';
import { useRequest } from 'ahooks';
import { getGenerateStatus } from '@/service/audioList';
import { useAudioStreamingStatus } from '@/hooks/useAudioStreamingStatus';
import useFolderNames from '@/hooks/useFolderNames';
import { AudioActionEllipseDropDown } from '../../AudioActionEllipseDropDown';
import { useDeleteTrack } from '@/hooks/useDeleteTrack';
import { useLikeOrDislike } from '@/hooks/useLikeOrDislike';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { AudioDetail } from '@/state/audio-detail-store';
import { WaveForm } from '../WaveForm';
import { useAudioListStore } from '@/state/audio-list-store';
import { useSongDetailDrawer } from '@/state/drawer-store';
import Lottie from 'lottie-react';
import jeneratingAnimation from '@/assets/animation/audioitem-jenerating.json';
import completeAnimation from '@/assets/animation/audioitem-complete.json';
import jenuineAnimation from '@/assets/animation/audioitem-jenuine.json';
import { useMachine } from '@xstate/react';
import { audioItemGenerateStatusMachine, GenerateShowStatusEnum } from './audioItemState';
import { toast } from 'sonner';

interface AudioListItemProps {
  audioList: AudioDto[];
  audioInfo: AudioDto;
  selectId: string | undefined;
  onSelect: (audioInfo: AudioDto) => void;
  viewportRef: React.MutableRefObject<HTMLDivElement | null>;
  setAudioItem: (id: string, updatedData: Partial<AudioDto>) => void;
  updateAudioDetail: (partialAudioDetail: Partial<AudioDetail>) => void;
  updateAudioInList: (val: { [x: string]: AudioDto | undefined }) => void;
  actionShowConfig?: {
    showFolder?: boolean;
    showTime?: boolean;
    showLike?: boolean;
    showEllipsisAction?: boolean;
  };
}

const AudioListItem: React.FC<AudioListItemProps> = ({
  audioList,
  audioInfo,
  selectId,
  onSelect,
  viewportRef,
  setAudioItem,
  updateAudioDetail,
  updateAudioInList,
  actionShowConfig = {
    showFolder: true,
    showTime: true,
    showLike: true,
    showEllipsisAction: true,
  },
}) => {
  const isActive = selectId === audioInfo.id;
  const { play, isPlaying, isLoading, pause, playAudioInfo, wave, currentTime } = useAudioPlayerStore();
  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'AUDIO',
    item: { ...audioInfo, type: 'AUDIO' },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }));

  const [state, send] = useMachine(audioItemGenerateStatusMachine);
  const generateShowStatus = state.value;
  const progressPercentage = state.context.progressPercentage;

  const playId = playAudioInfo?.id;
  const isCurrentSongPlaying = useMemo(() => {
    return playId === audioInfo.id;
  }, [playId, audioInfo]);

  const { isMobileOrPad } = useDimensions();
  // const [hasError, setHasError] = useState<boolean>(false);
  const { run, cancel } = useRequest(
    () => {
      if (audioInfo?.id?.startsWith('fe_track_')) {
        return Promise.reject();
      } else {
        return getGenerateStatus({ id: audioInfo.id });
      }
    },
    {
      pollingInterval: 2000,
      manual: true,
      onSuccess: (data) => {
        if (data?.data?.status === GenerateStatus.VALIDATED || data?.data?.hasError) {
          cancel();
        }
        setAudioItem(audioInfo.id, {
          ...data.data,
        });
        updateAudioDetail({
          ...data.data,
        });
      },
    },
  );
  const hasError = generateShowStatus === GenerateShowStatusEnum.Error;

  const isStreaming = generateShowStatus === GenerateShowStatusEnum.Generating;
  useAudioStreamingStatus({
    generateShowStatus: generateShowStatus,
    send: send,
    m3u8Url: audioInfo.url,
    totalSongTime: audioInfo.duration,
  });

  const prevIsStreaming = useRef(isStreaming);
  const [jenuine, setJenuine] = useState(false);
  useEffect(() => {
    if (prevIsStreaming.current && !isStreaming) {
      setJenuine(true);
    }
    prevIsStreaming.current = isStreaming;
  }, [isStreaming]);

  useEffect(() => {
    if (audioInfo.is_generated && !audioInfo.hasError) {
      send({ type: 'GENERATED' });
    }
    if (audioInfo.hasError) {
      send({ type: 'ERROR' });
    }
    if (!audioInfo.is_generated && !audioInfo.hasError) {
      run();
      send({ type: 'GENERATING' });
    }
  }, [audioInfo.is_generated, audioInfo.hasError]);

  const folderNames = useFolderNames(audioInfo?.folder_id);
  const [folderChangedAnimating, setFolderChangedAnimating] = useState(false);
  const preFolderName = useRef(folderNames);
  useEffect(() => {
    if (folderNames !== preFolderName?.current) {
      setFolderChangedAnimating(true);
      preFolderName.current = folderNames;
      const timer = setTimeout(() => {
        setFolderChangedAnimating(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [folderNames]);
  const { showDeleteDialog } = useDeleteTrack({ id: audioInfo.id });
  const { likeOrDislike } = useLikeOrDislike({ id: audioInfo.id });
  const { setSelectedInfo } = useAudioListStore((state) => ({
    setSelectedInfo: state.setSelectedInfo,
  }));
  const { setIsDrawerShow } = useSongDetailDrawer();
  const playOrPause = () => {
    if (!canBePlayed) {
      return;
    }
    if (isCurrentSongPlaying) {
      if (isLoading) {
        return;
      }
      if (isPlaying) {
        pause();
      } else {
        play(audioInfo, audioList);
      }
    } else {
      play(audioInfo, audioList);
    }
  };

  const renderStatus = () => {
    if (hasError) {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger>
              <GeneratingTag className="bg-[red]" name="ERROR"></GeneratingTag>
            </TooltipTrigger>
            <TooltipContent className="bg-[#fff]">
              <p>{audioInfo.error_message}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }
    if (isStreaming) {
      return (
        <div className="flex gap-[8px] text-jen-interactive-secondary items-center mr-[6px]">
          {isStreaming && progressPercentage !== 0 && <span>Ready to listen</span>}
          <Lottie className="w-[118px]" animationData={jeneratingAnimation} />
        </div>
      );
    }
    // jenuine only after isStreaming from true become false's 2 second
    if (jenuine) {
      return (
        <div className={cn('flex gap-[8px] text-jen-interactive-secondary items-center mr-[6px] ')}>
          <Lottie className="w-[30px]" animationData={completeAnimation} loop={false} />
          <Lottie
            className="w-[118px]"
            animationData={jenuineAnimation}
            loop={false}
            onComplete={() => {
              setJenuine(false);
            }}
          />
        </div>
      );
    }
    const { showFolder, showTime, showLike, showEllipsisAction } = actionShowConfig;
    return (
      <div className={cn(' text-jen-interactive-track flex gap-[4px]')}>
        {showFolder && (
          <span
            className={cn('mr-[24px] hidden lg:flex justify-center items-center gap-[4px] transition-colors duration-500 ', {
              'text-jen-interactive-primary': folderChangedAnimating,
            })}
          >
            <FolderFilledIcon fillColor="currentColor" /> {folderNames || 'All Tracks'}
          </span>
        )}
        {showTime && (
          <span className={cn('hidden lg:block text-[16px] text-jen-content-secondary')}>
            {isCurrentSongPlaying && isPlaying
              ? displayDuration(Math.max(0, +currentTime.toFixed(0)))
              : displayDuration(audioInfo.duration)}
          </span>
        )}
        {showLike && (
          <HeartIcon
            fillColor="currentColor"
            className={cn('', {
              'text-red-500': audioInfo.is_liked,
              invisible: isStreaming || hasError,
              'pointer-events-none': isStreaming || hasError,
            })}
            onClick={async (e) => {
              e.stopPropagation();
              const previousLikeState = audioInfo.is_liked;
              const changedToLikeState = !audioInfo.is_liked;
              // optimistic update
              try {
                updateAudioInList({
                  [audioInfo.id]: {
                    ...audioInfo,
                    is_liked: changedToLikeState,
                  },
                });
                await likeOrDislike();
              } catch (error) {
                toast.error('Network error. Please try again later!');
                updateAudioInList({
                  [audioInfo.id]: {
                    ...audioInfo,
                    is_liked: previousLikeState,
                  },
                });
              }
            }}
          />
        )}
        {showEllipsisAction && (
          <AudioActionEllipseDropDown
            onSelect={onSelect}
            audioInfo={audioInfo}
            hide={isStreaming || audioInfo.is_deleted || hasError}
            className={cn('', {
              // 'opacity-100': isActive,
              invisible: isStreaming || audioInfo.is_deleted || hasError,
            })}
          />
        )}

        <ChevRightFilledIcon
          className="block lg:hidden"
          onClick={(e) => {
            e.stopPropagation();
            setSelectedInfo(audioInfo);
            setIsDrawerShow(true);
          }}
        />
      </div>
    );
  };
  const handleChangeDuration = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      if (playAudioInfo?.id !== audioInfo.id) return;
      if (!playAudioInfo?.duration) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const width = rect.width;
      const percentage = clickX / width;
      const newTime = percentage * playAudioInfo.duration;
      wave?.wavesurfer?.setTime(newTime);
    },
    [playAudioInfo, audioInfo],
  );
  const canBePlayed = ((isStreaming && Boolean(progressPercentage)) || !isStreaming) && !hasError;

  return (
    <div
      ref={(node) => {
        if (isCurrentSongPlaying) {
          viewportRef.current = node;
        }
        if (!isMobileOrPad) {
          drag(node);
        }
      }}
      onClick={(e) => {
        if (isMobileOrPad) {
          playOrPause();
        } else {
          onSelect(audioInfo);
          handleChangeDuration(e);
        }
      }}
      className={cn(
        'relative  p-[8px] pr-[18px] mb-jen-spacing-8 lg:mb-[6px] overflow-hidden cursor-pointer box-border border border-transparent rounded-jen-spacing-8 group/audioItem  grid-rows-1 text-[8f8f90] bg-jen-background-tertiary hover:bg-white ',
        { ' bg-white border-jen-orange-100 border': isActive },
        {
          ' !bg-[#F2D1C8]': isCurrentSongPlaying && !isLoading && playAudioInfo?.duration,
        },
        isDragging ? ' opacity-40' : 'opacity-100',
      )}
    >
      {isCurrentSongPlaying && !isLoading && !!playAudioInfo?.duration && (
        <div
          style={{
            width: `${currentTime ? ((currentTime / playAudioInfo?.duration) * 100).toFixed(2) : 0}%`,
          }}
          className="w-full h-full bg-jen-orange absolute left-0 top-0 "
        ></div>
      )}
      {isStreaming && (
        <div
          style={{
            width: `${progressPercentage}%`,
          }}
          className="w-full h-full bg-[#0000001A] absolute left-0 top-0 transition-all duration-500"
        ></div>
      )}

      <div className="flex items-center gap-[8px]">
        <div
          className=" relative group/playButton"
          onClick={(e) => {
            e.stopPropagation();
            playOrPause();
            if (!isMobileOrPad) {
              onSelect(audioInfo);
            }
          }}
        >
          <div className=" w-[48px] h-[48px] rounded bg-cover bg-center" style={{ backgroundImage: `url(${CoverDefaultSrc})` }}></div>
          {canBePlayed && (
            <div
              className={cn(
                'hidden  absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-80 w-[30px] h-[30px] rounded-full lg:opacity-100   bg-jen-orange   justify-center items-center group-hover/playButton:flex',
                {
                  flex: isCurrentSongPlaying,
                },
              )}
            >
              <div className="text-white">
                <ControlIcon isCurrentSongPlaying={isCurrentSongPlaying} isLoading={isLoading} isPlaying={isPlaying} />
              </div>
            </div>
          )}
        </div>
        {audioInfo?.metadata?.type && <EditTag type={audioInfo?.metadata?.type} />}
        {audioInfo?.metadata?.is_bonus && <EditTag type="bonus" />}
        <div className="relative flex-1  flex flex-col justify-center min-w-0 ">
          <div className={cn('text-[14px] lg:text-[16px] text-jen-size-heading-04  text-jen-black-100 truncate w-full pr-[40px]')}>
            {audioInfo.name}
          </div>
        </div>
        <div className="relative  flex justify-center items-center">{renderStatus()}</div>
      </div>
      {!isMobileOrPad && (
        <div
          className={cn(' ', {
            'transition-all duration-1000 ease-in-out': jenuine,
            'h-0': !(generateShowStatus === GenerateShowStatusEnum.Generated),
            'h-[28px]': generateShowStatus === GenerateShowStatusEnum.Generated,
          })}
        >
          {generateShowStatus === GenerateShowStatusEnum.Generated && (
            <WaveForm styleFilterObj={audioInfo.stylefilterDetail} songId={audioInfo.id} duration={audioInfo.duration} />
          )}
        </div>
      )}
    </div>
  );
};

export const GeneratingTag: React.FC<{
  name: string | React.ReactNode;
  icon?: React.ReactNode;
  className?: string;
}> = ({ icon, name, className }) => {
  return (
    <div
      className={cn('flex items-center h-[28px] text-[12px] rounded-[6px] py-[2px] pl-[4px] pr-[8px] bg-[#ff5b27] text-white', className)}
    >
      {icon}
      {name}
    </div>
  );
};

export const FilterButton: React.FC<{
  isActive: boolean;
  name: string;
  onClick?: () => void;
}> = ({ isActive, name, onClick }) => {
  return (
    <div
      className={cn(
        'hidden py-jen-spacing-4 rounded-[2px] pl-jen-spacing-4 pr-jen-spacing-8 justify-between items-center bg-jen-light-grey group-hover/audioItem:flex group-hover/audioItem:bg-jen-light-grey hover:!bg-jen-orange-100 hover:text-black',
        {
          'flex !bg-jen-dark-grey-100': isActive,
        },
      )}
      onClick={onClick}
    >
      <FilterIcon fillColor="currentColor" />
      <span className="leading-[16px] relative top-[1px]">{name}</span>
    </div>
  );
};

const ControlIcon: React.FC<{
  isLoading: boolean;
  isPlaying: boolean;
  isCurrentSongPlaying: boolean;
}> = ({ isLoading, isPlaying, isCurrentSongPlaying }) => {
  if (isCurrentSongPlaying) {
    if (isLoading) {
      return <LoaderCircle className=" animate-spin" />;
    }
    if (isPlaying) {
      return <PauseIcon fillColor="#fff" />;
    }
    return <PlayIcon fillColor="#fff" />;
  }
  return <PlayIcon fillColor="#fff" />;
};

interface EditTagProps {
  type: string;
}

const EditTag: React.FC<EditTagProps> = ({ type }) => {
  const wordContent = {
    refill: (
      <>
        REFILL<sup className=" scale-75">TM</sup>
      </>
    ),
    inpaint: (
      <>
        REFILL<sup className=" scale-75">TM</sup>
      </>
    ),
    extend: <>EXTEND</>,
    intro: <>INTRO</>,
    outro: <>OUTRO</>,
    trim: <>TRIM</>,
    fadeout: <>FADEOUT</>,
    reimagine: <>REIMAGINE</>,
    bonus: <>BONUS TRACK</>,
  }[type];

  return (
    wordContent && (
      <div className="flex items-center relative px-[4px] h-[16px] text-[12px] font-pressura-mono text-jen-background-primary bg-[#111112] rounded-[2px]">
        {wordContent}
      </div>
    )
  );
};

export default AudioListItem;
